/**
 * Routes Firebase/Google network traffic around Capacitor's CapacitorHttp patch.
 *
 * With `CapacitorHttp: { enabled: true }`, Capacitor's native bridge replaces
 * `window.fetch` and mutates `XMLHttpRequest.prototype` so requests go through a
 * one-shot native bridge call.  That transport cannot stream, which breaks
 * Firestore's WebChannel (no messages sent or received).  Realtime Database is
 * unaffected because it uses WebSocket, which is why presence still worked.
 *
 * The bridge stashes the untouched functions on `window` before patching, so we
 * re-wrap both APIs and dispatch per request: Firebase/Google hosts get the
 * originals, everything else keeps the existing native behaviour so cookie-based
 * auth against our own API is untouched.
 */

const BYPASS_HOST_SUFFIXES = [
  // firestore, identitytoolkit, securetoken, firebaseinstallations, ...
  'googleapis.com',
  'firebaseio.com',
  'firebasedatabase.app',
  'firebaseapp.com',
  'gstatic.com',
];

type WebXhrOriginals = {
  open: XMLHttpRequest['open'];
  send: XMLHttpRequest['send'];
  abort: XMLHttpRequest['abort'];
  setRequestHeader: XMLHttpRequest['setRequestHeader'];
  getAllResponseHeaders: XMLHttpRequest['getAllResponseHeaders'];
  getResponseHeader: XMLHttpRequest['getResponseHeader'];
};

const BYPASS_FLAG = '__useNativeWebXhr';
const INSTALLED_FLAG = '__firebaseNetworkBypassInstalled';

function shouldBypass(rawUrl: string): boolean {
  try {
    const { hostname } = new URL(rawUrl, window.location.href);
    return BYPASS_HOST_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`),
    );
  } catch {
    return false;
  }
}

function urlFromFetchResource(resource: unknown): string {
  if (typeof resource === 'string') return resource;
  if (resource instanceof URL) return resource.toString();
  if (resource && typeof (resource as Request).url === 'string') {
    return (resource as Request).url;
  }
  return '';
}

export function installCapacitorFirebaseNetworkBypass(): void {
  if (typeof window === 'undefined') return;

  const win = window as unknown as Record<string, unknown>;
  const webFetch = win['CapacitorWebFetch'] as typeof fetch | undefined;
  const webXhr = win['CapacitorWebXMLHttpRequest'] as WebXhrOriginals | undefined;

  // Absent on web, or when CapacitorHttp patching is disabled: nothing to undo.
  if (typeof webFetch !== 'function' || !webXhr) return;
  if (win[INSTALLED_FLAG]) return;
  win[INSTALLED_FLAG] = true;

  const patchedFetch = window.fetch;
  window.fetch = function (
    resource: Parameters<typeof fetch>[0],
    options?: Parameters<typeof fetch>[1],
  ): Promise<Response> {
    const impl = shouldBypass(urlFromFetchResource(resource))
      ? webFetch
      : patchedFetch;
    return impl.call(window, resource, options);
  };

  // The bridge mutates XMLHttpRequest.prototype in place, so restoring the
  // constructor alone is not enough; each prototype method must dispatch based
  // on the URL the instance was opened with.
  const proto = XMLHttpRequest.prototype;
  const patched: WebXhrOriginals = {
    open: proto.open,
    send: proto.send,
    abort: proto.abort,
    setRequestHeader: proto.setRequestHeader,
    getAllResponseHeaders: proto.getAllResponseHeaders,
    getResponseHeader: proto.getResponseHeader,
  };

  const usesWebXhr = (xhr: XMLHttpRequest): boolean =>
    (xhr as unknown as Record<string, unknown>)[BYPASS_FLAG] === true;

  proto.open = function (
    this: XMLHttpRequest,
    ...args: Parameters<XMLHttpRequest['open']>
  ) {
    const [, url] = args;
    (this as unknown as Record<string, unknown>)[BYPASS_FLAG] = shouldBypass(
      String(url),
    );
    const impl = usesWebXhr(this) ? webXhr.open : patched.open;
    return (impl as (...a: unknown[]) => void).apply(this, args);
  } as XMLHttpRequest['open'];

  proto.send = function (
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const impl = usesWebXhr(this) ? webXhr.send : patched.send;
    return impl.call(this, body);
  };

  proto.abort = function (this: XMLHttpRequest) {
    const impl = usesWebXhr(this) ? webXhr.abort : patched.abort;
    return impl.call(this);
  };

  proto.setRequestHeader = function (
    this: XMLHttpRequest,
    header: string,
    value: string,
  ) {
    const impl = usesWebXhr(this)
      ? webXhr.setRequestHeader
      : patched.setRequestHeader;
    return impl.call(this, header, value);
  };

  proto.getAllResponseHeaders = function (this: XMLHttpRequest) {
    const impl = usesWebXhr(this)
      ? webXhr.getAllResponseHeaders
      : patched.getAllResponseHeaders;
    return impl.call(this);
  };

  proto.getResponseHeader = function (this: XMLHttpRequest, name: string) {
    const impl = usesWebXhr(this)
      ? webXhr.getResponseHeader
      : patched.getResponseHeader;
    return impl.call(this, name);
  };
}
